import { useLazyQuery, useMutation } from '@apollo/client'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { BulkActivateDeactivateListings } from 'bpm/api/graphQL/mutations/bulkActions.mutation'
import { GetBulkActionStatus } from 'bpm/api/graphQL/queries/bulkActions.queries'
import debounce from 'lodash/debounce'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'

import { useStyles } from './BulkActivateDeactivateDialog.styles'
import {
    BulkActionStatusData,
    BulkActionStatusVars,
    BulkActivateDeactivateData,
    BulkActivateDeactivateVars,
    DialogProps,
    DialogStage
} from './BulkActivateDeactivateDialog.types'
import { ConfirmActions, ConfirmContent } from './ConfirmStage'
import { FailureActions, FailureContent } from './FailureStage'
import { ProgressActions, ProgressContent } from './ProgressStage'

export type { DialogStage, DialogState, SelectedListing } from './BulkActivateDeactivateDialog.types'

const MAX_LIST_ROWS = 8
const ERROR_AUTO_CLOSE_MS = 1000
const SUCCESS_AUTO_CLOSE_MS = 500
const POLL_DEBOUNCE_MS = 1000
const SNACKBAR_ID_SUCCESS = 'bulkActivateDeactivateSuccess'
const SNACKBAR_ID_ERROR = 'bulkActivateDeactivateError'
const SNACKBAR_ID_PARTIAL = 'bulkActivateDeactivatePartial'
const DIALOG_TITLE_ID = 'bulk-activate-deactivate-title'
const STAGE_CONFIRM: DialogStage = 'confirm'
const STAGE_PROGRESS: DialogStage = 'progress'
const STAGE_FAILURE: DialogStage = 'failure'

const BulkActivateDeactivateDialog: FC<DialogProps> = ({
    dialog,
    onClose,
    onDialogChange,
    resetSelect,
}) => {
    const classes = useStyles()
    const { showGlobalSnackbar } = useGlobalSnackbar()
    const isMountedRef = useRef(true)
    const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([])

    useEffect(() => {
        return () => {
            isMountedRef.current = false
            timeoutIdsRef.current.forEach(clearTimeout)
        }
    }, [])

    const [pollTick, setPollTick] = useState(0)

    const isActivate = dialog?.kind === 'activate'
    const verb = isActivate ? 'Activate' : 'Deactivate'
    const target = isActivate ? 'Active' : 'Inactive'
    const eligible = dialog?.eligible ?? []
    const skip = dialog?.skip ?? []
    const stage = dialog?.stage ?? 'confirm'
    const failedNames = dialog?.failedNames ?? []
    const progressPercentage = dialog?.progressPercentage ?? 0
    const showList = eligible.length + skip.length <= MAX_LIST_ROWS

    // Refs for latest values captured inside Apollo callbacks to avoid stale closures
    const eligibleRef = useRef(eligible)
    const verbRef = useRef(verb)
    const stageRef = useRef(stage)
    useEffect(() => { eligibleRef.current = eligible }, [eligible])
    useEffect(() => { verbRef.current = verb }, [verb])
    useEffect(() => { stageRef.current = stage }, [stage])

    const scheduleClose = useCallback((delay: number, beforeClose?: () => void) => {
        const id = setTimeout(() => {
            if (!isMountedRef.current) return
            beforeClose?.()
            onClose()
        }, delay)
        timeoutIdsRef.current.push(id)
    }, [onClose])

    const [bulkActivateDeactivate] = useMutation<BulkActivateDeactivateData, BulkActivateDeactivateVars>(
        BulkActivateDeactivateListings,
        {
            onCompleted: (data) => {
                const instanceId =
                    data?.startBulkActivateDeactivateProductListingDetails?.instanceId
                if (instanceId) {
                    onDialogChange((prev) => ({
                        ...prev,
                        stage: STAGE_PROGRESS,
                        instanceId,
                        progressPercentage: 0,
                    }))
                }
            },
            onError: (error) => {
                showGlobalSnackbar({
                    variables: {
                        message: error?.message || `Error starting bulk ${verbRef.current.toLowerCase()}`,
                        variant: 'error',
                        id: SNACKBAR_ID_ERROR,
                    },
                })
                onClose()
            },
        }
    )

    const [getBulkActionProgress] = useLazyQuery<BulkActionStatusData, BulkActionStatusVars>(
        GetBulkActionStatus,
        {
            onCompleted: (data) => {
                if (stageRef.current !== STAGE_PROGRESS) return
                const result = data?.getBulkProductListingDetailStatus
                const pct: number = result?.progressPercentage ?? 0

                onDialogChange((prev) => ({ ...prev, progressPercentage: pct }))
                setPollTick((t) => t + 1)

                if (result?.errorMessage) {
                    showGlobalSnackbar({
                        variables: {
                            message: result.errorMessage,
                            variant: 'error',
                            id: SNACKBAR_ID_ERROR,
                        },
                    })
                    scheduleClose(ERROR_AUTO_CLOSE_MS)
                    return
                }

                if (pct === 100) {
                    const failed: { productName: string }[] =
                        result?.result?.failedBulkProductDetailTransaction ?? []

                    if (failed.length > 0) {
                        showGlobalSnackbar({
                            variables: {
                                message: `${failed.length} product(s) could not be ${verbRef.current.toLowerCase()}d. See dialog for details.`,
                                variant: 'warning',
                                id: SNACKBAR_ID_PARTIAL,
                            },
                        })
                        onDialogChange((prev) => ({
                            ...prev,
                            stage: STAGE_FAILURE,
                            failedNames: failed.map((f) => f.productName),
                        }))
                    } else {
                        const count = eligibleRef.current.length
                        const noun = count === 1 ? 'product' : 'products'
                        showGlobalSnackbar({
                            variables: {
                                message: `Successfully ${verbRef.current.toLowerCase()}d ${count} ${noun}.`,
                                variant: 'success',
                                id: SNACKBAR_ID_SUCCESS,
                            },
                        })
                        scheduleClose(SUCCESS_AUTO_CLOSE_MS, resetSelect)
                    }
                }
            },
            onError: (error) => {
                showGlobalSnackbar({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: SNACKBAR_ID_ERROR,
                    },
                })
                scheduleClose(ERROR_AUTO_CLOSE_MS)
            },
        }
    )

    // Stable ref so the debounced function always calls the latest query instance
    const getBulkActionProgressRef = useRef(getBulkActionProgress)
    useEffect(() => { getBulkActionProgressRef.current = getBulkActionProgress }, [getBulkActionProgress])

    const fetchProgress = useRef(
        debounce(
            (instanceId: string) => {
                getBulkActionProgressRef.current({ variables: { input: { instanceId } } })
            },
            POLL_DEBOUNCE_MS,
            { trailing: true }
        )
    ).current

    useEffect(() => {
        if (stage === STAGE_PROGRESS && dialog?.instanceId && progressPercentage < 100) {
            fetchProgress(dialog.instanceId)
        }
        return () => fetchProgress.cancel()
    }, [stage, dialog?.instanceId, progressPercentage, pollTick, fetchProgress])

    const handleConfirm = useCallback(() => {
        if (!dialog) return
        bulkActivateDeactivate({
            variables: {
                input: {
                    listingIds: eligible.map((l) => l.listingId),
                    activate: isActivate,
                },
            },
        })
    }, [dialog, eligible, isActivate, bulkActivateDeactivate])

    const handleRetry = useCallback(() => {
        // TODO #2680113: backend returns productName only — match is name-based.
        // If two listings share a name and one fails, both are retried.
        const failedProducts = eligible.filter((l) => failedNames.includes(l.productName))
        onDialogChange(() => ({
            kind: dialog!.kind,
            stage: STAGE_CONFIRM,
            eligible: failedProducts,
            skip: [],
            instanceId: null,
            progressPercentage: 0,
            failedNames: [],
        }))
    }, [eligible, failedNames, dialog, onDialogChange])

    if (!dialog) return null

    const title =
        stage === STAGE_FAILURE
            ? `${failedNames.length} product${failedNames.length === 1 ? '' : 's'} could not be updated`
            : `${verb} ${eligible.length} product${eligible.length === 1 ? '' : 's'}?`

    const handleDialogClose = stage === STAGE_PROGRESS ? undefined : onClose

    return (
        <Dialog
            open
            maxWidth="sm"
            fullWidth
            disableBackdropClick={stage === STAGE_PROGRESS}
            disableEscapeKeyDown={stage === STAGE_PROGRESS}
            onClose={handleDialogClose}
            aria-labelledby={DIALOG_TITLE_ID}
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogTitle id={DIALOG_TITLE_ID} className={classes.dialogTitle}>{title}</DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {stage === STAGE_CONFIRM && (
                    <ConfirmContent
                        eligible={eligible}
                        skip={skip}
                        target={target}
                        isActivate={isActivate}
                        showList={showList}
                    />
                )}
                {stage === STAGE_PROGRESS && (
                    <ProgressContent progressPercentage={progressPercentage} />
                )}
                {stage === STAGE_FAILURE && (
                    <FailureContent
                        eligible={eligible}
                        failedNames={failedNames}
                        target={target}
                    />
                )}
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                {stage === STAGE_CONFIRM && (
                    <ConfirmActions
                        onClose={onClose}
                        onConfirm={handleConfirm}
                        eligible={eligible}
                        isActivate={isActivate}
                        verb={verb}
                    />
                )}
                {stage === STAGE_PROGRESS && (
                    <ProgressActions isActivate={isActivate} />
                )}
                {stage === STAGE_FAILURE && (
                    <FailureActions
                        onClose={onClose}
                        onRetry={handleRetry}
                        failedNames={failedNames}
                    />
                )}
            </DialogActions>
        </Dialog>
    )
}

export default BulkActivateDeactivateDialog
